/**
 * The phone calendar's data seam: the minute clock, the lanes' own data
 * hook for today, the open alerts, the schedule actions and the place in
 * the URL. `PhoneCalendar` draws what this hands it and fetches nothing.
 *
 * The schedule actions (`useScheduleActions`, shared with the laptop
 * Agenda) are built only when the screen says this viewer may edit
 * schedules (`scheduleEditsAllowed`, the very value the laptop lanes'
 * skip / pause callbacks are gated on); otherwise they are absent and the
 * sheet draws no button for them.
 */
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useRobots } from '../../../api/hooks';
import type { TaskState } from '../../../api/rmf-client';
import type { MaintenanceWindow, ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import { ALERTS_ROLES, hasAnyRole } from '../../../auth/roles';
import { useAuth } from '../../../auth/useAuth';
import type { SiteTimeZone } from '../../../state/attention/words';
import { robotKeyOf, type LaneItem } from './lane-items';
import { dateKeyOf, dayWindow } from './lane-time';
import { PhoneCalendar, type PhoneActions } from './PhoneCalendar';
import { PhoneTaskCancel } from './PhoneTaskCancel';
import { groupMembership } from './phone-model';
import { useCalendarIssues, useScheduleActions } from './useScheduleActions';
import { parsePhoneUrl, writePhoneUrl, type PhoneUrlState } from './phone-url';
import { useCalendarLaneData } from './useCalendarLaneData';
import { useMinuteNow } from './useMinuteNow';
import { useLiftCalendar } from './useLiftCalendar';
import type { BlockTimePreset } from './BlockTimeDialog';

export function PhoneCalendarContainer(props: {
  siteId: string;
  timeZone: string;
  timeZoneSource: SiteTimeZone['source'];
  tasks: readonly TaskState[];
  occurrences: readonly ScheduleOccurrence[];
  schedules: readonly TaskSchedule[];
  maintenance: readonly MaintenanceWindow[];
  /** The laptop's `scheduleEditsAllowed`: role + flag + readiness. */
  scheduleEditsAllowed: boolean;
  scheduleReadFailed?: boolean;
  onOpenTask?: (item: LaneItem) => void;
  /** The screen's tab bar, drawn in the calendar's sticky head. */
  tabBar?: ReactNode;
  /** The screen's banners, drawn under the sticky head. */
  notices?: ReactNode;
  /** Booking a maintenance window (a supervisor), as on a laptop. */
  onCreateMaintenance?: (range: { startMs: number; endMs: number; reason?: string }) => void;
  /** May this viewer set lift rules on the calendar. */
  liftRulesAllowed?: boolean;
  blockPreset?: BlockTimePreset | null;
}) {
  const nowMs = useMinuteNow();
  const todayKey = dateKeyOf(nowMs, props.timeZone);
  const window = useMemo(() => dayWindow(todayKey, props.timeZone), [todayKey, props.timeZone]);
  const data = useCalendarLaneData({
    siteId: props.siteId,
    window,
    nowMs,
    tasks: props.tasks,
    occurrences: props.occurrences,
    schedules: props.schedules,
    maintenance: props.maintenance,
  });

  // -- the place, in the URL ------------------------------------------------
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const url = parsePhoneUrl(params);
  /** A sheet THIS view pushed: closing it goes Back, so the history
   *  entry it made is consumed rather than left for Back to reopen. */
  const pushedSheet = useRef(false);
  const write = useCallback(
    (patch: Partial<PhoneUrlState>, replace: boolean) =>
      setParams((prev) => writePhoneUrl(prev, patch), { replace }),
    [setParams],
  );
  const onTab = useCallback((tab: PhoneUrlState['tab']) => write({ tab, sheet: null }, true), [write]);
  const onOpenSheet = useCallback(
    (id: string) => {
      if (url.sheet) {
        // From one sheet to another (a burst's delivery): same entry.
        write({ sheet: id }, true);
        return;
      }
      pushedSheet.current = true;
      write({ sheet: id }, false);
    },
    [url.sheet, write],
  );
  const onCloseSheet = useCallback(() => {
    if (pushedSheet.current) {
      pushedSheet.current = false;
      navigate(-1);
      return;
    }
    write({ sheet: null }, true);
  }, [navigate, write]);

  // -- robot problems: the server's open alerts ----------------------------
  const auth = useAuth();
  const membership = useMemo(
    () => groupMembership(data.items, data.groups, data.robots),
    [data.items, data.groups, data.robots],
  );
  const issues = useCalendarIssues({
    timeZone: props.timeZone,
    timeZoneSource: props.timeZoneSource,
    nowMs,
    nameGroup: membership.nameGroup,
  });

  // -- robots that cannot take work -----------------------------------------
  const robots = useRobots();
  const unavailable = useMemo(() => {
    const out = new Set<string>();
    for (const r of robots.data ?? []) {
      if (r.status === 'offline' || r.status === 'error' || r.status === 'pending-localize') {
        const key = robotKeyOf(r.fleet, r.name);
        if (key) out.add(key);
      }
    }
    return out;
  }, [robots.data]);

  // -- actions (`useScheduleActions`, shared with the laptop Agenda) --------
  const actions: PhoneActions = useScheduleActions(props.scheduleEditsAllowed, props.timeZone);
  const reset = actions.resetStatus;

  const openSheet = useCallback(
    (id: string) => {
      reset();
      onOpenSheet(id);
    },
    [reset, onOpenSheet],
  );
  const closeSheet = useCallback(() => {
    reset();
    onCloseSheet();
  }, [reset, onCloseSheet]);

  const liftCalendar = useLiftCalendar();

  return (
    <PhoneCalendar
      // Booking from a phone came with lift rules: with them off, as it was.
      onCreateMaintenance={liftCalendar.on ? props.onCreateMaintenance : undefined}
      liftCalendar={
        !liftCalendar.on
          ? undefined
          : props.liftRulesAllowed
            ? liftCalendar
            : { windowOf: liftCalendar.windowOf }
      }
      blockPreset={props.blockPreset}
      timeZone={props.timeZone}
      nowMs={nowMs}
      data={data}
      schedules={props.schedules}
      occurrences={props.occurrences}
      issues={issues}
      unavailable={unavailable}
      tab={url.tab}
      onTab={onTab}
      sheet={url.sheet}
      onOpenSheet={openSheet}
      onCloseSheet={closeSheet}
      actions={actions}
      onOpenTask={props.onOpenTask}
      renderTaskCancel={(t) => <PhoneTaskCancel task={t} />}
      onOpenAlerts={hasAnyRole(auth.roles, ALERTS_ROLES) ? () => navigate('/alerts') : undefined}
      scheduleReadFailed={props.scheduleReadFailed}
      tabBar={props.tabBar}
      notices={props.notices}
      mySub={auth.user?.id ?? null}
    />
  );
}
